import * as t from 'io-ts';
import type { Signal } from '@bayer-int/portletce-aclibv2';

import type {
    BWQueryParameters,
    FilterData,
    FilterIdToSignalMapping,
    ResolvedListenToDefinition,
    ResolvedListenToDefinitions,
    SettingValueMap,
} from 'portlet/models';
import { BWSystemDefinition } from 'portlet/models/query/systems/bw';
import { DatabricksDefinitionC } from 'portlet/models/query/systems/databricks/databricks';

import type { DatabricksParameters } from './databricks/types';
import type {
    QueryChartInteractionDatapoint,
    QueryChartInteractionDatapointMultiSelect,
} from '../../../services/InteractionToFilterService/QueryChartInteractionDatapoint';
import { QueryType } from './modules';

export type PortletQueryId = string;

/**
 * Portlet filtering is closely related to query, e.g. a filter may be assigned
 * to a query parameter that will be sent in the data request. Due to that,
 * when configuring query/filter interactions, there may be extra information that
 * could be later extracted and used. This is what this `context` is for.
 */
export type MatchedListenToQueryContext = Partial<{
    internalKey: string;
}>;

export const MatchedListenToQueryContext = { default: (): MatchedListenToQueryContext => ({}) };

export interface PortletQueryDefinition<QueryMetadata, QuerySignalValue> {
    codec: t.Type<QueryMetadata>;

    /** function responsible for building the signal that can be later used in specs */
    build: (
        metadata: QueryMetadata,
        settings: Partial<SettingValueMap>,
        filters: FilterData[],
        signals: Signal[],
        listenToSignalMapping: FilterIdToSignalMapping,
    ) => QuerySignalValue | null;

    /**
     * mostly required for 'jump to AO', but it has to be in a common interface
     * queries that do not support that, can just return `null`
     */
    externalLink: (
        metadata: QueryMetadata,
        settings: Partial<SettingValueMap>,
        filters: FilterData[],
        signals: Signal[],
        listenToSignalMapping: FilterIdToSignalMapping,
    ) => string | null;

    /** Tries to convert chart interaction to a filter */
    interactionToFilter: (
        metadata: QueryMetadata,
        listenTo: ResolvedListenToDefinitions,
        datapoint: QueryChartInteractionDatapoint | QueryChartInteractionDatapointMultiSelect,
    ) => FilterData | null;

    /**
     * Tries to pick listenTo based on interaction.
     * May return additional context/metadata (see MatchedListenToQueryContexts).
     */
    interactionToListenTo: (
        metadata: QueryMetadata,
        listenTo: ResolvedListenToDefinitions,
        datapoint: QueryChartInteractionDatapoint | QueryChartInteractionDatapointMultiSelect,
    ) => {
        matchedListenTo: ResolvedListenToDefinition;
        context: MatchedListenToQueryContext;
    } | null;
}
//make an union when adding new systems
export const PortletQueryC = t.union([BWSystemDefinition.codec, DatabricksDefinitionC.codec]);

export type PortletQuery = t.TypeOf<typeof PortletQueryC>;

//make an union when adding new systems
export type PortletQueryParams = BWQueryParameters | DatabricksParameters;

export const PortletQuery: PortletQueryDefinition<PortletQuery, PortletQueryParams> = {
    codec: PortletQueryC,
    build: (metadata, ...args) => {
        switch (metadata.type) {
            case QueryType.BW: {
                return BWSystemDefinition.build(metadata, ...args);
            }
            case QueryType.Databricks: {
                return DatabricksDefinitionC.build(metadata, ...args);
            }
            default: {
                return null;
            }
        }
    },
    externalLink: (metadata, ...args) => {
        switch (metadata.type) {
            case QueryType.BW: {
                return BWSystemDefinition.externalLink(metadata, ...args);
            }
            case QueryType.Databricks: {
                return DatabricksDefinitionC.externalLink(metadata, ...args);
            }
            default: {
                return null;
            }
        }
    },
    interactionToFilter: (metadata, ...args) => {
        switch (metadata.type) {
            case QueryType.BW: {
                return BWSystemDefinition.interactionToFilter(metadata, ...args);
            }
            case QueryType.Databricks: {
                return DatabricksDefinitionC.interactionToFilter(metadata, ...args);
            }
            default: {
                return null;
            }
        }
    },
    interactionToListenTo: (metadata, ...args) => {
        switch (metadata.type) {
            case QueryType.BW: {
                return BWSystemDefinition.interactionToListenTo(metadata, ...args);
            }
            case QueryType.Databricks: {
                return DatabricksDefinitionC.interactionToListenTo(metadata, ...args);
            }
            default: {
                return null;
            }
        }
    },
};
